import React from "react";
import { compose } from "recompose";
import { withSnackbar } from "notistack";
import styles from "../styles";
import { withStyles } from "@material-ui/core";
import { postData } from "../../../../../../api/componentAPIs";
import constants from "./constants";
import appConstants from "../../../../../../appConstants";
import ShipmentsModel from "./components/ShipmentsModel";
import Events from "../../../../../../utilities/EventEmitter";
import { trackEvent } from "../../../../../common/AppInsightsEventTracking";
import { getMoveStatus, getShipmentsInTrailer } from "./utils";
import { Drop_Hook_Move_Page_Names } from "../../constants";
import MoveBills from "./components/MoveBills";
import { isEqual } from "lodash/lang";

class MoveBillsContainer extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      fromTrailer: null,
      movedToTrailerNumber: null,
      selectedStopType: null,
      selectedShipments: [],
      errorInfo: this.getDefaultErrorInfo(),
      moveBillError: null,
      moveBillsDelayed: null,
      isMoveBillsInProgress: false,
      fromTrailersList: [],
      toTrailersList: [],
    };
    this.timer = null;
  }

  componentDidMount() {
    Events.on("MoveBills", this.checkMoveBillsStatus);
    trackEvent("Move_Bills", this.props.userProfile, { event: "Viewed" });
    this.generateDataOptions();
  }

  componentDidUpdate(prevProps) {
    if (
      !isEqual(prevProps.routeData, this.props.routeData) ||
      this.props.trailerList !== prevProps.trailerList
    ) {
      this.generateDataOptions();
    }
  }

  componentWillUnmount() {
    Events.removeListener("MoveBills", this.checkMoveBillsStatus);
  }

  onMoveBillsProgressTimeOut = () => {
    this.timer = setTimeout(() => {
      this.setState({ moveBillsDelayed: true });
    }, appConstants.ONE_MINUTE_TIME_OUT);
  };

  checkMoveBillsStatus = (data) => {
    const { success, message, driverId, movedToTrailerNumber } = data;

    if (this.props.routeData.driverId?.toUpperCase() !== driverId?.toUpperCase()) {
      return;
    }
    if (success) {
      this.props.enqueueSnackbar(
        `Bills moved successfully from trailer number ${this.state.fromTrailer.label} to ${movedToTrailerNumber}`,
        {
          variant: "success",
        },
      );
      this.props.onClose();
    } else {
      this.props.enqueueSnackbar(`Move Bills Failed`, {
        variant: "error",
      });
      this.setState({
        isMoveBillsInProgress: false,
        moveBillsDelayed: false,
        moveBillsError: message.error,
      });
    }
    clearTimeout(this.timer);
  };

  getDefaultErrorInfo() {
    return {
      fromTrailer: null,
      movedToTrailerNumber: null,
      selectedStopType: null,
    };
  }

  generateDataOptions = () => {
    const {
     trailerList,
      currentDriverRoutes,
    } = this.props;
    const fromTrailersList = currentDriverRoutes
      .filter((trailer) => Boolean(trailer.dispatchId))
      .map((trailer) => ({
        label: trailer.trailerNumber,
        value: trailer.trailerNumber,
        isActiveRoute: trailer.isActiveRoute,
      }));

    const toTrailersList = trailerList.map((trailer) => ({
      label: trailer.trailerNumber,
      value: trailer.trailerNumber,
      trailerStatus: trailer.trailerStatus,
      labelSuffix: `(${trailer.trailerStatus})`,
    }));
    this.setState({
      fromTrailersList,
      toTrailersList
    })
  };

  handleChange = (field) => (value) => {
    this.setState((prevState) => ({
      [field]: value,
      errorInfo: {
        ...prevState.errorInfo,
        [field]: this.getFieldError(field, value),
      },
      moveBillError: null,
    }));
  };

  handleBillType = (value) => {
    this.setState(
      (state) => ({
        selectedStopType: value,
        errorInfo: {
          ...state.errorInfo,
          selectedStopType: this.getFieldError("selectedStopType", value),
        },
        moveBillError: null,
      }),
      this.selectStops,
    );
  };

  selectStops = () => {
    const { selectedStopType, fromTrailer } = this.state;
    if (!Boolean(selectedStopType)) {
      return;
    }
    const {
      allShipments,
      allDeliveryShipments,
      allPickedUpShipments,
      individualSelectedShipments,
    } = getShipmentsInTrailer(fromTrailer, this.props.currentDriverRoutes);
    const {
      STOPS: { ALL_STOPS, ALL_DELIVERIES, ALL_PICKUPS, SELECT_STOPS },
    } = constants;
    switch (selectedStopType.value) {
      case ALL_STOPS:
        this.setSelectedShipments([...allShipments]);
        break;
      case ALL_DELIVERIES:
        this.setSelectedShipments([...allDeliveryShipments]);
        break;
      case ALL_PICKUPS:
        this.setSelectedShipments([...allPickedUpShipments]);
        break;
      case SELECT_STOPS:
        this.setSelectedShipments([...individualSelectedShipments]);
        break;
    }
  };

  setSelectedShipments = (shipments) => {
    this.setState({
      selectedShipments: shipments,
    });
  };

  onShipmentSelected = (shipmentId) => () => {
    const { selectedShipments } = this.state;
    const newChecked = selectedShipments.map((bill) => {
      if (bill.shipment.shipmentId === shipmentId) {
        return {
          ...bill,
          isChecked: !bill.isChecked,
        };
      }
      return bill;
    });

    this.setState({
      selectedShipments: newChecked,
    });
  };

  getProDetails = () => {
    const { selectedShipments, fromTrailer } = this.state;
    let deliveryPros = [],
      returnPros = [],
      pickupPros = [];
    const movedFromRouteId = this.props.currentDriverRoutes.find(
      (route) => route.trailerNumber === this.state.fromTrailer?.value,
    ).routeId;
    selectedShipments
      .filter((bill) => bill.isChecked)
      .forEach((bill) => {
        if (bill.stop.stopType === appConstants.STOP_TYPE.Pickup) {
          pickupPros.push({
            pickupId: bill.shipment.pickupId,
            proNumber: bill.shipment.proId,
          });
        } else if (bill.stop.stopType === appConstants.STOP_TYPE.Delivery) {
          const proObj = {
            shipmentId: bill.shipment.shipmentId,
            proNumber: bill.shipment.proId,
          };
          if (bill.shipment?.deliveryCode === constants.RETURN_DELIVERY_CODE) {
            return returnPros.push(proObj);
          }
          deliveryPros.push(proObj);
        }
      });
    const moveStatus = getMoveStatus({
      deliveryPros,
      returnPros,
      pickupPros,
      fromTrailer,
      currentDriverRoutes: this.props.currentDriverRoutes,
    });
    return {
      deliveryPros,
      returnPros,
      pickupPros,
      movedFromRouteId,
      moveStatus,
    };
  };

  handleSaveBills = () => {
    const errorInfo = this.getErrorInfo();
    const error = Object.values(errorInfo).filter((value) => value).length;
    if (!error) {
      const {
        deliveryPros,
        returnPros,
        pickupPros,
        movedFromRouteId,
        moveStatus,
      } = this.getProDetails();
      if (!(deliveryPros.length || returnPros.length || pickupPros.length)) {
        this.setState({
          moveBillError: constants.MESSAGES.BILL_REQUIRED,
        });
        return;
      }
      const inputObj = {
        terminalCode: this.props.userProfile.terminal.code,
        driverId: this.props.routeData.driverId,
        movedFromRouteId: movedFromRouteId,
        movedToTrailerNumber: this.state.movedToTrailerNumber?.value,
        deliveryPros: deliveryPros,
        returnPros: returnPros,
        pickupPros: pickupPros,
        moveStatus: moveStatus,
      };
      this.setState(
        {
          isMoveBillsInProgress: true,
          moveBillsError: null,
        },
        this.onMoveBillsProgressTimeOut,
      );
      trackEvent("Move_Bills", this.props.userProfile, {
        event: "Move_Bills",
        eventData: inputObj,
      });
      postData("MoveBills", inputObj)
        .then((response) => {
          if (response && Math.trunc(response.status / 100) === 2);
          else {
            this.handleMoveBillsError();
          }
        })
        .catch((_error) => {
          this.handleMoveBillsError();
        });
    }
  };

  handleNextClick = () => {
    const errorInfo = this.getErrorInfo();
    const error = Object.values(errorInfo).filter((value) => value).length;
    if (!error) {
      this.props.setPageName(Drop_Hook_Move_Page_Names.MOVE_BILLS);
    }
  };

  handleMoveBillsError = () => {
    this.props.enqueueSnackbar(`Error while processing Move Bills`, {
      variant: "error",
    });
    clearTimeout(this.timer);
    this.setState({
      isMoveBillsInProgress: false,
      moveBillsDelayed: null,
    });
  };

  getErrorInfo = () => {
    let errorInfo = {};
    Object.keys(this.state.errorInfo).forEach((fieldName) => {
      errorInfo[fieldName] = this.getFieldError(
        fieldName,
        this.state[fieldName],
      );
    });
    this.setState({
      errorInfo,
    });
    return errorInfo;
  };

  getMovedToTrailerError = (value) => {
    if (this.props.allowedTrailerStatusesToMoveBills.includes(value.trailerStatus)) {
      return null;
    }
    return constants.MESSAGES.INVALID_TO_TRAILER_STATUS;
  };

  getFieldError = (field, value) => {
    switch (field) {
      case "movedToTrailerNumber":
        return value && value.label.trim()
          ? this.getMovedToTrailerError(value)
          : constants.MESSAGES.REQUIRED;
      case "selectedStopType":
        return value && value.label.trim() ? null : constants.MESSAGES.REQUIRED;
      default:
        return null;
    }
  };

  render() {
    const { isSelectShipmentPage, onClose, isSectionDisabled, routeData } = this.props;
    const {
      errorInfo,
      selectedStopType,
      selectedShipments,
      movedToTrailerNumber,
      isMoveBillsInProgress,
      moveBillsDelayed,
      moveBillError,
    } = this.state;
    return (
      <>
        {isSelectShipmentPage ? (
          <ShipmentsModel
            data={selectedShipments}
            onShipmentSelected={this.onShipmentSelected}
            errorInfo={errorInfo}
            toTrailersList={this.state.toTrailersList}
            handleChange={this.handleChange}
            movedToTrailerNumber={movedToTrailerNumber}
            handleSaveBills={this.handleSaveBills}
            isMoveBillsInProgress={isMoveBillsInProgress}
            moveBillsDelayed={moveBillsDelayed}
            moveBillError={moveBillError}
            onClose={onClose}
          />
        ) : (
          <MoveBills
            fromTrailersList={this.state.fromTrailersList}
            toTrailersList={this.state.toTrailersList}
            routeData={routeData}
            handleChange={this.handleChange}
            errorInfo={errorInfo}
            movedToTrailerNumber={movedToTrailerNumber}
            selectedStopType={selectedStopType}
            handleBillType={this.handleBillType}
            handleNextClick={this.handleNextClick}
            isSectionDisabled={isSectionDisabled}
          />
        )}
      </>
    );
  }
}

export default compose(withStyles(styles), withSnackbar)(MoveBillsContainer);
